import type { EntityKind } from "./refs.ts";
import { PLUGIN_OWNED_FIELDS } from "./entities.ts";

export type EntityFieldRequirement = {
	name: string;
	kind: "string" | "number" | "boolean" | "array" | "object" | "enum" | "any";
	required: boolean;
	nullable?: boolean;
	refEntity?: EntityKind;
	min?: number;
	gt?: number;
	minLength?: number;
	values?: readonly string[];
	description?: string;
};

export type CatalogEntityKind =
	| "items"
	| "attacks"
	| "recipes"
	| "currencies"
	| "stats"
	| "quests";

export type EntitySpec = {
	kind: CatalogEntityKind;

	singular: string;
	fields: EntityFieldRequirement[];
};

const RANGE_TIERS = ["Melee", "Medium", "Long"] as const;
const AOE_TIERS = ["Small", "Medium", "Large"] as const;
const STAT_DISPLAYS = ["hidden", "bar", "tracker"] as const;
const STAT_IMPACTS = ["Attack", "Defense", "Vitality", "Speed", "Production", "None"] as const;
const QUEST_TYPES = ["Journal", "Daily", "Other", "Match"] as const;

const RAW_ENTITY_SPECS: EntitySpec[] = [
	{
		kind: "items",
		singular: "item",
		fields: [
			{ name: "name", kind: "string", required: true, minLength: 1 },
			{ name: "modelPaths", kind: "array", required: false },
			{ name: "category", kind: "string", required: false },
			{ name: "maxQuantity", kind: "number", required: false, gt: 0 },
			{ name: "wanderRadius", kind: "number", required: false, gt: 0 },
			{ name: "minTeamsToStart", kind: "number", required: false, gt: 0 },
			{ name: "maxTeams", kind: "number", required: false, gt: 0 },
			{
				name: "allowedTeamSizes",
				kind: "array",
				required: false,
				minLength: 1,
				description: "Allowed team sizes for Matchmaker items, e.g. [1, 2].",
			},
			{ name: "countdownSeconds", kind: "number", required: false, gt: 0 },
			{
				name: "matchQuest",
				kind: "string",
				required: false,
				refEntity: "quests",
				description: "Matchmaker only: the Match quest that defines win/defeat rules.",
			},
			{
				name: "stats",
				kind: "object",
				required: false,
				description: "Map of statId -> numeric value.",
			},
			{ name: "attacks", kind: "array", required: false, refEntity: "attacks" },
			{
				name: "folderId",
				kind: "string",
				required: false,
				nullable: true,
				description: "Optional CMS folder id for organizing items; null moves to root.",
			},
		],
	},
	{
		kind: "attacks",
		singular: "attack",
		fields: [
			{ name: "name", kind: "string", required: true, minLength: 1 },
			{ name: "range", kind: "enum", required: true, values: RANGE_TIERS },
			{ name: "cooldown", kind: "number", required: true, gt: 0 },
			{ name: "animationId", kind: "string", required: false },
			{ name: "vfxModelPath", kind: "string", required: false },
			{ name: "aoeRadius", kind: "enum", required: false, values: AOE_TIERS },
			{ name: "costStat", kind: "string", required: false, refEntity: "stats" },
			{ name: "costAmount", kind: "number", required: false, min: 0 },
			{
				name: "damage",
				kind: "array",
				required: true,
				minLength: 1,
				description:
					"Damage entries: [{ statKey, amount > 0, type: 'absolute'|'percent' }].",
			},
			{ name: "hitStunDuration", kind: "number", required: false, min: 0 },
			{ name: "swingSoundId", kind: "string", required: false },
			{ name: "impactSoundId", kind: "string", required: false },
			{ name: "screenShakeIntensity", kind: "number", required: false, min: 0 },
			{ name: "projectileModelPath", kind: "string", required: false },
			{ name: "projectileSpeed", kind: "number", required: false, gt: 0 },
			{ name: "projectileMaxDistance", kind: "number", required: false, gt: 0 },
			{ name: "projectileHoming", kind: "boolean", required: false },
			{ name: "projectilePiercing", kind: "boolean", required: false },
			{
				name: "folderId",
				kind: "string",
				required: false,
				nullable: true,
				description: "Optional CMS folder id for organizing attacks; null moves to root.",
			},
		],
	},
	{
		kind: "recipes",
		singular: "recipe",
		fields: [
			{ name: "name", kind: "string", required: true, minLength: 1 },
			{
				name: "inputs",
				kind: "array",
				required: false,
				description:
					"Recipe entries: { type: 'item', itemId, quantity } or { type: 'currency', currencyId, amount }.",
			},
			{
				name: "outputs",
				kind: "array",
				required: true,
				minLength: 1,
				description: "Recipe entries, at least one. Same shape as inputs.",
			},
			{ name: "developerProductId", kind: "string", required: false },
			{ name: "description", kind: "string", required: false },
		],
	},
	{
		kind: "currencies",
		singular: "currency",
		fields: [
			{ name: "name", kind: "string", required: true, minLength: 1 },
			{ name: "modelPath", kind: "string", required: false },
			{ name: "order", kind: "number", required: true },
			{ name: "passiveRatePerHour", kind: "number", required: false, min: 0 },
			{ name: "maxAmount", kind: "number", required: false, gt: 0 },
		],
	},
	{
		kind: "stats",
		singular: "stat",
		fields: [
			{ name: "name", kind: "string", required: true, minLength: 1 },
			{ name: "order", kind: "number", required: true },
			{ name: "showInUi", kind: "boolean", required: false },
			{ name: "display", kind: "enum", required: true, values: STAT_DISPLAYS },
			{ name: "impact", kind: "enum", required: true, values: STAT_IMPACTS },
			{ name: "iconAssetId", kind: "string", required: false },
			{ name: "showTierChangeMessage", kind: "boolean", required: false },
			{ name: "regenPerSecond", kind: "number", required: false, min: 0 },
		],
	},
	{
		kind: "quests",
		singular: "quest",
		fields: [
			{ name: "name", kind: "string", required: true, minLength: 1 },
			{ name: "description", kind: "string", required: false },
			{ name: "type", kind: "enum", required: true, values: QUEST_TYPES },
			{
				name: "winObjectives",
				kind: "array",
				required: true,
				minLength: 1,
				description:
					"Win objectives: [{ action, itemId, amount }]. amount is number > 0, except Die uses 'one'|'all'.",
			},
			{
				name: "loseObjectives",
				kind: "array",
				required: false,
				description:
					"Lose objectives: [{ action, itemId, amount }]. Match quests require at least one.",
			},
			{
				name: "rewards",
				kind: "array",
				required: true,
				minLength: 1,
				description:
					"Rewards: { type:'item', itemId, quantity } | { type:'currency', currencyId, amount } | { type:'stat', statKey, mode, value }.",
			},
			{ name: "order", kind: "number", required: false },
			{ name: "timeLimit", kind: "number", required: false, gt: 0 },
			{ name: "repeatable", kind: "boolean", required: false },
		],
	},
];

function stripPluginOwned(spec: EntitySpec): EntitySpec {
	const owned = new Set<string>(
		PLUGIN_OWNED_FIELDS[spec.kind as EntityKind] ?? [],
	);
	return {
		...spec,
		fields: spec.fields.filter((field) => !owned.has(field.name)),
	};
}

export const ENTITY_SPECS: EntitySpec[] = RAW_ENTITY_SPECS.map(stripPluginOwned);

export const CATALOG_ENTITY_KINDS = ENTITY_SPECS.map(
	(spec) => spec.kind,
) as readonly CatalogEntityKind[];
